import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import "@fontsource/roboto/900.css";
import React, { Suspense } from "react";
import * as ReactDOM from "react-dom/client";
import { App } from "./app/App";
import {
  PushNotificationProvider,
  registerServiceWorker,
} from "shared-ts-push-notification";
import { boot } from "./app/boot/boot";
import {
  AppBoot,
  BootPage,
  CenteredCircularProgress,
  UserSettingsLoadingPage,
} from "shared-ts-ui-app-boot";
import {
  AppThemeInitializer,
  AppThemeProvider,
} from "shared-ts-ui-app-theme-provider";
import { darkTheme } from "./app/theme/darkTheme";
import { lightTheme } from "./app/theme/lightTheme";
import { I18NextProvider } from "shared-ts-utils-i18n";
import { fallbackLanguages, languages } from "./languages";
import {
  ServerSettingsStorage,
  UserSettingsProvider,
} from "shared-ts-utils-user-settings";
import { MuiLocalizationProvider } from "shared-ts-mui";
import { appInfo } from "./appInfo";
import i18next from "i18next";
import { WindowSizeProvider } from "shared-ts-ui-responsive";
import { Box } from "@mui/material";
import { SystemDao } from "firefly-my-chimney-ts-system";
import { LoginProvider } from "shared-ts-ui-identity";
import { McUser, McUserDto } from "firefly-my-chimney-ts-user";
import {
  PostHogConsentProvider,
  PostHogIdentifier,
  PostHogInitializer,
} from "shared-ts-posthog";
import { LanguageUserSettingProvider } from "shared-ts-language";
import { LocaleProvider } from "shared-ts-locale";
import { SentryConsentProvider, SentryInitializer } from "shared-ts-sentry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Settings } from "luxon";
import { ResponsiveMyChimneyLogo } from "firefly-my-chimney-ts-shared";
import {
  FetchBuilder,
  problemDetailsAwareServerErrorFactory,
  ServerErrorMiddlewareFactory,
  UnauthenticatedAwareServerErrorMiddleware,
} from "shared-ts-utils-fetch";
import { AppInfoProvider } from "shared-ts-app-info";
import { AppLayout, AppLayoutModalProvider } from "shared-ts-ui-app-layout";
import { PrivacyPolicyPage } from "my-chimney-ts-privacy-policy";
import { RegisterUser } from "my-chimney-ts-register-user";
import { AcceptUserInvitation } from "my-chimney-ts-accept-user-invitation";
import { ConfirmUser } from "my-chimney-ts-confirm-user";
import { ChangePasswordPage } from "my-chimney-ts-set-new-password";
import { LoginPage } from "my-chimney-ts-login";
import { ConditionalWrap } from "shared-ts-ui-react";
import { UAParser } from "ua-parser-js";
import { UAParserUtils } from "shared-ts-utils-ua-parser";

const uaParsedResult = UAParser();
console.info(
  "UAParser result: \n" + JSON.stringify(uaParsedResult, null, "  ")
);
const isMobile = UAParserUtils.isMobile(uaParsedResult);
const pushNotificationsEnabled =
  localStorage.getItem("pushNotificationsEnabled") === "true" && !isMobile;
console.info("UserAgent: isMobile = " + isMobile);
// Register service worker early for push notifications
if (pushNotificationsEnabled && "serviceWorker" in navigator) {
  registerServiceWorker("/service-worker.js")
    .then((registration) => {
      console.log("Service Worker registered with scope:", registration.scope);
    })
    .catch((error) => {
      console.error("Service Worker registration failed:", error);
    });
}

FetchBuilder.defaultServerErrorFactory = new ServerErrorMiddlewareFactory(
  problemDetailsAwareServerErrorFactory
).use(
  new UnauthenticatedAwareServerErrorMiddleware([
    "/api/system/check-authentication",
  ])
).handle;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});
const userSettingsStorage = new ServerSettingsStorage();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const systemDao = new SystemDao();

root.render(
  <Suspense fallback={<CenteredCircularProgress />}>
    <QueryClientProvider client={queryClient}>
      <AppInfoProvider
        appInfo={appInfo}
        getBuildNumber={systemDao.getBuildNumber}
      >
        {(appInfo) => {
          Settings.throwOnInvalid =
            appInfo.buildVersion === "local-development";
          return (
            <UserSettingsProvider>
              {(settings) => {
                return (
                  <AppThemeProvider
                    initialTheme={settings.readString("theme")}
                    themes={{
                      dark: darkTheme,
                      light: lightTheme,
                    }}
                  >
                    <AppLayout disableUserSelect={isMobile}>
                      <WindowSizeProvider>
                        <I18NextProvider
                          language={settings.readString("language") ?? "nb-NO"}
                          languages={languages}
                          fallbackLng={fallbackLanguages}
                          useHttpBackend
                          debug={
                            localStorage.getItem("i18next.debug") === "true"
                          }
                        >
                          <LoginProvider<McUserDto>
                            loginComponent={(props) => (
                              <LoginPage
                                {...props}
                                sx={{
                                  height: "100%",
                                }}
                              />
                            )}
                            privacyPolicy={{
                              path: "/privacy-policy",
                              component: () => (
                                <PrivacyPolicyPage
                                  sx={{
                                    height: "100%",
                                  }}
                                />
                              ),
                            }}
                            registerUser={{
                              path: "/register-user",
                              component: () => (
                                <RegisterUser
                                  sx={{
                                    height: "100%",
                                  }}
                                />
                              ),
                            }}
                            confirmUser={{
                              path: "/confirm-user",
                              component: () => (
                                <ConfirmUser
                                  sx={{
                                    height: "100%",
                                  }}
                                />
                              ),
                            }}
                            acceptUserInvitation={{
                              path: "/accept-user-invitation",
                              component: () => (
                                <AcceptUserInvitation
                                  sx={{
                                    height: "100%",
                                  }}
                                />
                              ),
                            }}
                            resetPassword={{
                              path: "/change-password",
                              component: () => (
                                <ChangePasswordPage
                                  sx={{
                                    height: "100%",
                                  }}
                                />
                              ),
                            }}
                            authenticationCheck={systemDao.checkAuthentication}
                            userMapper={(userDto) => new McUser(userDto)}
                          >
                            {(user) => {
                              return (
                                <UserSettingsProvider
                                  storage={userSettingsStorage}
                                  loadingComponent={
                                    <UserSettingsLoadingPage
                                      appNameTranslated={i18next.t(
                                        "My Chimney",
                                        {
                                          ns: "my-chimney-ts-translations",
                                        }
                                      )}
                                    />
                                  }
                                >
                                  <LanguageUserSettingProvider
                                    defaultLanguage={"nb-NO"}
                                  >
                                    {(language) => {
                                      return (
                                        <LocaleProvider locale={language}>
                                          <MuiLocalizationProvider
                                            language={language}
                                          >
                                            <AppThemeInitializer>
                                              <SentryConsentProvider>
                                                <SentryInitializer
                                                  dsn={
                                                    "https://7c2637aa9965efd42f3841702d9b548d@o4504604658761728.ingest.sentry.io/4506517905801216"
                                                  }
                                                  feedbackIntegration={true}
                                                >
                                                  <PostHogConsentProvider>
                                                    <PostHogInitializer
                                                      apiKey={
                                                        "phc_VbxRV3Y7anFGuiq4CpwrJ6p9ezlYfEU1ZrNQk29gUF3"
                                                      }
                                                      options={{
                                                        api_host:
                                                          "https://eu.i.posthog.com",
                                                      }}
                                                    >
                                                      <PostHogIdentifier
                                                        user={user}
                                                      >
                                                        <AppBoot
                                                          boot={boot}
                                                          bootPage={({
                                                            messages,
                                                            error,
                                                          }) => (
                                                            <BootPage
                                                              appIcon={
                                                                <ResponsiveMyChimneyLogo />
                                                              }
                                                              appName={i18next.t(
                                                                "My Chimney",
                                                                {
                                                                  ns: "my-chimney-ts-translations",
                                                                }
                                                              )}
                                                              messages={
                                                                messages
                                                              }
                                                              error={error}
                                                            />
                                                          )}
                                                          app={(bootResult) => {
                                                            return (
                                                              <AppLayoutModalProvider>
                                                                <ConditionalWrap
                                                                  condition={
                                                                    pushNotificationsEnabled
                                                                  }
                                                                  wrap={(
                                                                    children
                                                                  ) => (
                                                                    <PushNotificationProvider>
                                                                      {children}
                                                                    </PushNotificationProvider>
                                                                  )}
                                                                >
                                                                  <App
                                                                    bootResult={
                                                                      bootResult
                                                                    }
                                                                  />
                                                                </ConditionalWrap>
                                                              </AppLayoutModalProvider>
                                                            );
                                                          }}
                                                          appWhenUnauthenticated={() => {
                                                            return (
                                                              <Box>
                                                                Should not
                                                                happen
                                                              </Box>
                                                            );
                                                          }}
                                                        />
                                                      </PostHogIdentifier>
                                                    </PostHogInitializer>
                                                  </PostHogConsentProvider>
                                                </SentryInitializer>
                                              </SentryConsentProvider>
                                            </AppThemeInitializer>
                                          </MuiLocalizationProvider>
                                        </LocaleProvider>
                                      );
                                    }}
                                  </LanguageUserSettingProvider>
                                </UserSettingsProvider>
                              );
                            }}
                          </LoginProvider>
                        </I18NextProvider>
                      </WindowSizeProvider>
                    </AppLayout>
                  </AppThemeProvider>
                );
              }}
            </UserSettingsProvider>
          );
        }}
      </AppInfoProvider>
    </QueryClientProvider>
  </Suspense>
);
